// Minted Market Store — sandbox state machine for §6 + §6.1.
//
// This module is the sole source of truth for Mint-created markets in
// the sandbox. It is deliberately decoupled from the `mockMarkets.ts`
// pool: mock markets are authored recipes that seed the Lens Grid, while
// Minted markets are viewer-created entries that traverse the full §6.1
// lifecycle (PENDING → ACTIVE → STALE → VOIDED_BY_SOVEREIGN |
// AUTO_REFUNDED) in compressed sandbox time.
//
// The real production cut will move this to Postgres — `minted_markets`
// keyed on the creator's citizen id, with the state machine enforced via
// row-level triggers and settlement hooks. Here we just want the flow
// visible: mint debits a sandbox bond pool, Citizens vouch, Sovereigns
// can Veto in the window, and Auto-Refund fires deterministically.

import { IdentityLevel, IdentityTier } from './identity';
import { RegionLens } from './socialPulse';

// ─── Types ──────────────────────────────────────────────────────────────

export type ResolutionSource = 'Newswire' | 'AI-Consensus' | 'Weather-Oracle';

// Terminal states (VOIDED_BY_SOVEREIGN, AUTO_REFUNDED) are strictly
// one-way — the state machine never leaves them. PROTOCOL_SPEC.md §6.1.
export type MintStatus =
  | 'PENDING'              // just minted, waiting for 2 Citizen vouches
  | 'ACTIVE'               // vouched, appears on the Global Lens
  | 'STALE'                // scheduledResolutionAt passed with no oracle signal
  | 'VOIDED_BY_SOVEREIGN'  // 3-Sovereign quorum Veto — bond + fees burned
  | 'AUTO_REFUNDED';       // T+72h timer fired — bond returned

// v1.9.1 Multi-Way Core · §1. Every Minted market carries an explicit
// outcome list. Binary markets are just a 2-row outcome list with
// { YES, NO }; multi-way markets extend to 8 rows plus optional
// "The Field" catch-all. Probabilities initialise at 1/N equally and
// sum to 1.0 at mint (the store enforces, not the UI).
export interface Outcome {
  id:          string;     // stable key — drives React list + price-tracking
  label:       string;     // display text
  probability: number;     // 0..1, Σ outcomes = 1.0 (The Field counted when includeField)
}

// The Field catch-all always occupies this fixed outcome id when
// includeField is true — referenced by the Discovery Bonus and
// Resolution Hierarchy paths so the id is stable across renders.
export const FIELD_OUTCOME_ID = '__field__';

export const MIN_OUTCOMES = 2;
export const MAX_OUTCOMES = 8;

export interface MintedMarket {
  id:                     string;
  title:                  string;
  lens:                   RegionLens;
  resolutionSource:       ResolutionSource;
  primaryOracleUrl:       string;          // v2.3 — URL / agency the creator nominates as the truth feed (free-form text)
  resolutionClause:       string;          // v2.3 — Reality Blueprint narrative: the exact conditions under which this truth becomes knowable
  estimatedVolume:        number;          // v2.3 — creator's expected OC volume; feeds the Projection Feed (Maker-Fee earnings estimate)
  creationBond:           number;          // OC locked at mint
  makerFeeRate:           number;          // v2.3.2 Economic Calibration — 1.0 % resolution fee (was 0.5 %), snapshot at mint so a spec change doesn't retro-rewrite live markets
  outcomes:               Outcome[];       // v1.9.1 — named outcomes (≥2, ≤8); The Field is separate via includeField
  includeField:           boolean;         // v1.9.1 — when true, an implicit "The Field" outcome wins if no named entity resolves
  resolutionHierarchy:    string[];        // v1.9.1 — ordered source list (first-match-wins) for multi-way adjudication
  creatorId:              string;
  creatorTier:            IdentityTier;    // snapshot at mint (§6 invariant)
  createdAt:              number;          // ms epoch
  scheduledResolutionAt:  number;          // ms epoch — sandbox-compressed
  staleAt:                number | null;   // ms epoch when STALE transitioned, else null
  vouches:                Set<string>;     // voucher ids; threshold by lens promotes to ACTIVE
  vetoSignatures:         string[];        // Sovereign ids who signed Veto (sandbox: first click suffices)
  makerFeeAccrued:        number;          // simulated Maker Fee built up during ACTIVE
  status:                 MintStatus;
  fastTrackedBy:          string | null;   // Sovereign id who fast-tracked (self-mint or inbound bypass); null = normal Citizen queue
}

// ─── Sandbox timing ─────────────────────────────────────────────────────
//
// Compression ratio: 1 real second ≈ 1 simulated hour. Keeps the full
// lifecycle under ~100 seconds so a tester sees PENDING → ACTIVE →
// STALE → (VOIDED | AUTO_REFUNDED) in a single session rather than three
// real days.
//
// Knobs are deliberately module-level constants rather than props so the
// spec numbers stay legible in code review. If the ratified timings in
// §6.1 change, update here and in the spec in lockstep.

export const SANDBOX_SECOND_PER_HOUR_RATIO = 1;
const HOUR_MS = 1000 * SANDBOX_SECOND_PER_HOUR_RATIO; // 1000 ms per simulated hour

// Mint → scheduled resolution: 15 sim-hours. Short enough that testers
// don't stare at a PENDING card for minutes, long enough to vouch before
// the market stales out.
const MINT_TO_SCHEDULED_HOURS = 15;

// §6.1: Veto window opens at T+24h, closes (AUTO_REFUND fires) at T+72h.
const VETO_OPENS_AT_HOURS   = 24;
const AUTO_REFUND_AT_HOURS  = 72;

// Sandbox quorum shortcut: a single Sovereign click simulates the full
// 3-signature quorum. The UI labels this as "QUORUM SIMULATED" so the
// tester understands the compression.
const SANDBOX_VETO_QUORUM = 1;

// ─── Sandbox bond pool ──────────────────────────────────────────────────
//
// Mint bond deduction runs against a module-level sandbox balance, not
// the user's Supabase `profiles.balance` pledge stipend (100 OC) — those
// numbers are an order of magnitude smaller than a 500 OC creation bond
// by design. Keeping the pools separate also means a Mint never
// contaminates the real P2P ledger.

export const MINT_BOND_DEFAULT = 500;
export const MINT_POOL_STARTING_OC = 5000;

// v2.3.1 Dynamic Minting Cockpit — Reputation discounts the Creation
// Bond. Every DP the creator has earned shaves 0.5 OC off the bond,
// down to a hard floor of 100 OC. Formula:
//   bond = max(BOND_HARD_FLOOR, MINT_BOND_DEFAULT − dp × BOND_DP_COEFFICIENT)
// The 0.5 coefficient (up from the v2.3 brief's 0.1) is the ratified
// rate — a Citizen at the 150 DP threshold mints for 425 OC, a heavy
// pledger at 800 DP hits the 100 OC floor.
export const BOND_HARD_FLOOR      = 100;
export const BOND_DP_COEFFICIENT  = 0.5;

export function computeRequiredBond(dp: number): number {
  const discount = Math.max(0, dp) * BOND_DP_COEFFICIENT;
  return Math.max(BOND_HARD_FLOOR, MINT_BOND_DEFAULT - discount);
}

// v2.3.2 Economic Calibration — the Maker Fee is now the 1.0 %
// settlement cut the Architect ratified on 2026-04-22, up from the
// 0.5 % v1.9 Sound-Foundation figure. The rate is snapshotted onto
// every MintedMarket at mint (`makerFeeRate`) so existing markets
// keep their original economics even when this constant moves; only
// newly-minted markets use the updated rate.
export const MAKER_FEE_RATE = 0.01;

// §6 Tiered Vouching — thresholds scale with lens reach. LOCAL is the
// lightest promotion lift; GLOBAL (Macro) is the highest. A Sovereign
// vouch bypasses the Citizen queue entirely on any lens (Fast-Track), so
// the GLOBAL "OR 1 Sovereign Vouch" row in the spec is handled by the
// fast-track path rather than a second entry in this table.
export const VOUCHES_REQUIRED_BY_LENS: Record<RegionLens, number> = {
  LOCAL:  2,
  ASIA:   3,
  EUROPE: 3,
  GLOBAL: 5,
};

export function vouchesRequiredFor(lens: RegionLens): number {
  return VOUCHES_REQUIRED_BY_LENS[lens];
}

interface UserBondState {
  balance:     number;   // current OC in the sandbox Mint pool
  bondsLocked: number;   // sum of bonds currently held by PENDING/ACTIVE/STALE markets
}

const userBonds: Map<string, UserBondState> = new Map();

function ensureUserBond(userId: string): UserBondState {
  let s = userBonds.get(userId);
  if (!s) {
    s = { balance: MINT_POOL_STARTING_OC, bondsLocked: 0 };
    userBonds.set(userId, s);
  }
  return s;
}

export function getMintBondBalance(userId: string): number {
  return ensureUserBond(userId).balance;
}

export function getMintBondLocked(userId: string): number {
  return ensureUserBond(userId).bondsLocked;
}

// ─── Market store ───────────────────────────────────────────────────────

const MARKETS: MintedMarket[] = [];

let idCounter = 0;
function mintId(): string {
  idCounter += 1;
  return `mint_${Date.now().toString(36)}_${idCounter}`;
}

type Listener = () => void;
const listeners: Set<Listener> = new Set();

function notify(): void {
  // forEach instead of for-of to stay ES5-safe without enabling
  // downlevelIteration (CRA build target can't iterate Sets directly).
  listeners.forEach(fn => fn());
}

export function subscribeMintActivity(fn: Listener): () => void {
  listeners.add(fn);
  ensureTickerRunning();
  return () => {
    listeners.delete(fn);
    // Leave the ticker running — the state machine is module-level, so a
    // component unmounting shouldn't halt the lifecycle on in-flight
    // markets. Consistent with mockMarkets.ts.
  };
}

// ─── Veto-specific event stream ────────────────────────────────────────
// The generic `listeners` set above fires on any mint mutation (mint,
// vouch, veto, lifecycle tick). Veto Purge (v2.2 §2) needs a narrower
// signal — a single pulse per successful VOIDED_BY_SOVEREIGN transition
// carrying the market id, so the UI can RGB-glitch the grid and dissolve
// the specific card. Keeping this separate avoids replaying the glitch
// on every unrelated notify().
type VetoListener = (marketId: string) => void;
const vetoListeners: Set<VetoListener> = new Set();

function emitVeto(marketId: string): void {
  vetoListeners.forEach(fn => fn(marketId));
}

export function subscribeVetoEvents(fn: VetoListener): () => void {
  vetoListeners.add(fn);
  return () => { vetoListeners.delete(fn); };
}

// ─── Mint ───────────────────────────────────────────────────────────────

export interface MintInput {
  title:               string;
  lens:                RegionLens;
  resolutionSource:    ResolutionSource;
  primaryOracleUrl:    string;     // v2.3 — creator-nominated truth feed (URL or agency name)
  resolutionClause:    string;     // v2.3 — Reality Blueprint narrative; min 12 chars
  estimatedVolume:     number;     // v2.3 — OC volume estimate for the Projection Feed
  creationBond:        number;
  creatorId:           string;
  creatorTier:         IdentityTier;
  outcomeLabels:       string[];   // v1.9.1 — named outcomes (≥2, ≤8); store assigns ids + equal probabilities
  includeField:        boolean;    // v1.9.1 — include "The Field" catch-all
  resolutionHierarchy: string[];   // v1.9.1 — ordered source list (first-match-wins)
}

export type MintResult =
  | { ok: true;  market: MintedMarket }
  | { ok: false; reason:
      | 'INSUFFICIENT_BOND_POOL' | 'TIER_TOO_LOW' | 'BOND_BELOW_MIN' | 'TITLE_TOO_SHORT'
      | 'TOO_FEW_OUTCOMES' | 'TOO_MANY_OUTCOMES' | 'OUTCOME_LABEL_EMPTY' | 'OUTCOME_LABELS_DUPLICATE'
      | 'RESOLUTION_HIERARCHY_EMPTY' | 'PRIMARY_ORACLE_EMPTY' | 'RESOLUTION_CLAUSE_TOO_SHORT' };

// v2.3 Reality Blueprint — the Resolution Clause needs enough prose to
// name the winning condition. 12 chars is intentionally loose: "X wins
// the election" is 18, so this catches empty-ish inputs without
// policing style.
export const RESOLUTION_CLAUSE_MIN_CHARS = 12;

// v1.9.1 · equal-probability distribution across the active outcome set.
// Called with the number of named outcomes + whether The Field is in
// play. Rounds to 4 dp so the UI readout doesn't show 0.333333... — the
// last slot absorbs any rounding residue so Σ = exactly 1.0.
export function buildEqualOutcomes(labels: string[], includeField: boolean): Outcome[] {
  const slotCount = labels.length + (includeField ? 1 : 0);
  if (slotCount === 0) return [];
  const each   = Math.round((1 / slotCount) * 10000) / 10000;
  const rows: Outcome[] = labels.map((label, i) => ({
    id:          `out_${i + 1}`,
    label:       label.trim(),
    probability: each,
  }));
  if (includeField) {
    rows.push({ id: FIELD_OUTCOME_ID, label: 'The Field', probability: each });
  }
  // Absorb rounding residue into the last row so the sum is exact.
  const sum      = rows.reduce((acc, r) => acc + r.probability, 0);
  const residual = Math.round((1 - sum) * 10000) / 10000;
  if (residual !== 0 && rows.length > 0) {
    rows[rows.length - 1].probability =
      Math.round((rows[rows.length - 1].probability + residual) * 10000) / 10000;
  }
  return rows;
}

export function mintMarket(input: MintInput): MintResult {
  if (input.creatorTier < IdentityLevel.CITIZEN)      return { ok: false, reason: 'TIER_TOO_LOW' };
  // v2.3.1 Dynamic Bond — the floor is BOND_HARD_FLOOR (100 OC), not
  // MINT_BOND_DEFAULT. The UI computes the exact required bond from
  // the creator's DP via computeRequiredBond() and ships that value,
  // but the store only enforces the hard floor so a discount never
  // trips the guard.
  if (input.creationBond < BOND_HARD_FLOOR)           return { ok: false, reason: 'BOND_BELOW_MIN' };
  if (input.title.trim().length < 8)                  return { ok: false, reason: 'TITLE_TOO_SHORT' };

  const labels = input.outcomeLabels.map(l => l.trim());
  if (labels.length < MIN_OUTCOMES)                   return { ok: false, reason: 'TOO_FEW_OUTCOMES' };
  if (labels.length > MAX_OUTCOMES)                   return { ok: false, reason: 'TOO_MANY_OUTCOMES' };
  if (labels.some(l => l.length === 0))               return { ok: false, reason: 'OUTCOME_LABEL_EMPTY' };

  // Duplicate detection — case-insensitive so "Yes" and "yes" don't both
  // qualify. forEach + object bag instead of Set.size for ES5 safety.
  const seen: Record<string, true> = {};
  let duplicate = false;
  labels.forEach(l => {
    const key = l.toLowerCase();
    if (seen[key]) duplicate = true;
    seen[key] = true;
  });
  if (duplicate)                                      return { ok: false, reason: 'OUTCOME_LABELS_DUPLICATE' };

  const hierarchy = input.resolutionHierarchy.map(s => s.trim()).filter(s => s.length > 0);
  if (hierarchy.length === 0)                         return { ok: false, reason: 'RESOLUTION_HIERARCHY_EMPTY' };

  const primaryOracleUrl = input.primaryOracleUrl.trim();
  if (primaryOracleUrl.length === 0)                  return { ok: false, reason: 'PRIMARY_ORACLE_EMPTY' };

  const resolutionClause = input.resolutionClause.trim();
  if (resolutionClause.length < RESOLUTION_CLAUSE_MIN_CHARS) {
    return { ok: false, reason: 'RESOLUTION_CLAUSE_TOO_SHORT' };
  }

  const bond = ensureUserBond(input.creatorId);
  if (bond.balance < input.creationBond)              return { ok: false, reason: 'INSUFFICIENT_BOND_POOL' };

  bond.balance     -= input.creationBond;
  bond.bondsLocked += input.creationBond;

  const now = Date.now();
  // §6 Sovereign Fast-Track · Self-Minting Auto-Vouch.
  // A Sovereign (Tier 3) minting their own market is auto-vouched at
  // T+0 and enters ACTIVE instantly. The Sovereign Seal is applied by
  // setting fastTrackedBy to the creator. Citizens still route through
  // the PENDING → vouched-to-threshold path.
  const isSovereignSelfMint = input.creatorTier >= IdentityLevel.SOVEREIGN;
  const initialVouches: Set<string> = new Set();
  if (isSovereignSelfMint) initialVouches.add(input.creatorId);

  const market: MintedMarket = {
    id:                    mintId(),
    title:                 input.title.trim(),
    lens:                  input.lens,
    resolutionSource:      input.resolutionSource,
    primaryOracleUrl,
    resolutionClause,
    estimatedVolume:       Math.max(0, Number.isFinite(input.estimatedVolume) ? input.estimatedVolume : 0),
    creationBond:          input.creationBond,
    makerFeeRate:          MAKER_FEE_RATE,
    outcomes:              buildEqualOutcomes(labels, input.includeField),
    includeField:          input.includeField,
    resolutionHierarchy:   hierarchy,
    creatorId:             input.creatorId,
    creatorTier:           input.creatorTier,
    createdAt:             now,
    scheduledResolutionAt: now + MINT_TO_SCHEDULED_HOURS * HOUR_MS,
    staleAt:               null,
    vouches:               initialVouches,
    vetoSignatures:        [],
    makerFeeAccrued:       0,
    status:                isSovereignSelfMint ? 'ACTIVE' : 'PENDING',
    fastTrackedBy:         isSovereignSelfMint ? input.creatorId : null,
  };
  MARKETS.unshift(market);
  notify();
  return { ok: true, market };
}

// ─── Vouch ──────────────────────────────────────────────────────────────

export type VouchResult =
  | { ok: true }
  | { ok: false; reason: 'NOT_FOUND' | 'TIER_TOO_LOW' | 'ALREADY_VOUCHED' | 'CREATOR_CANNOT_VOUCH' | 'WRONG_STATE' };

export function vouchForMarket(marketId: string, voucherId: string, voucherTier: IdentityTier): VouchResult {
  const m = MARKETS.find(x => x.id === marketId);
  if (!m)                                           return { ok: false, reason: 'NOT_FOUND' };
  if (m.status !== 'PENDING')                       return { ok: false, reason: 'WRONG_STATE' };
  if (voucherTier < IdentityLevel.CITIZEN)          return { ok: false, reason: 'TIER_TOO_LOW' };
  if (voucherId === m.creatorId)                    return { ok: false, reason: 'CREATOR_CANNOT_VOUCH' };
  if (m.vouches.has(voucherId))                     return { ok: false, reason: 'ALREADY_VOUCHED' };

  m.vouches.add(voucherId);

  // §6 Sovereign Fast-Track · Inbound Bypass.
  // A single Sovereign Vouch promotes the market to ACTIVE on any lens
  // regardless of current Citizen vouch count. The Sovereign Seal is
  // applied. Citizen vouches count toward the tiered threshold by lens.
  if (voucherTier >= IdentityLevel.SOVEREIGN) {
    m.status        = 'ACTIVE';
    m.fastTrackedBy = voucherId;
  } else if (m.vouches.size >= vouchesRequiredFor(m.lens)) {
    m.status = 'ACTIVE';
  }
  notify();
  return { ok: true };
}

// ─── Veto ───────────────────────────────────────────────────────────────

export type VetoResult =
  | { ok: true }
  | { ok: false; reason: 'NOT_FOUND' | 'TIER_TOO_LOW' | 'NOT_STALE' | 'VETO_WINDOW_NOT_OPEN' | 'VETO_WINDOW_CLOSED' | 'ALREADY_SIGNED' };

export function vetoMarket(marketId: string, signerId: string, signerTier: IdentityTier): VetoResult {
  const m = MARKETS.find(x => x.id === marketId);
  if (!m)                                           return { ok: false, reason: 'NOT_FOUND' };
  if (m.status !== 'STALE')                         return { ok: false, reason: 'NOT_STALE' };
  if (signerTier < IdentityLevel.SOVEREIGN)         return { ok: false, reason: 'TIER_TOO_LOW' };
  if (m.staleAt === null)                           return { ok: false, reason: 'NOT_STALE' };

  const now     = Date.now();
  const opens   = m.staleAt + VETO_OPENS_AT_HOURS  * HOUR_MS;
  const closes  = m.staleAt + AUTO_REFUND_AT_HOURS * HOUR_MS;
  if (now < opens)                                  return { ok: false, reason: 'VETO_WINDOW_NOT_OPEN' };
  if (now >= closes)                                return { ok: false, reason: 'VETO_WINDOW_CLOSED' };
  if (m.vetoSignatures.includes(signerId))          return { ok: false, reason: 'ALREADY_SIGNED' };

  m.vetoSignatures.push(signerId);
  let voided = false;
  if (m.vetoSignatures.length >= SANDBOX_VETO_QUORUM) {
    // §6.1 VOIDED_BY_SOVEREIGN — bond + accrued Maker Fees burned.
    const creator = ensureUserBond(m.creatorId);
    creator.bondsLocked = Math.max(0, creator.bondsLocked - m.creationBond);
    // Bond is burned — NOT added back to `balance`. Same for makerFeeAccrued.
    m.status = 'VOIDED_BY_SOVEREIGN';
    voided = true;
  }
  notify();
  if (voided) emitVeto(m.id);
  return { ok: true };
}

// ─── Lifecycle ticker ───────────────────────────────────────────────────
//
// Runs at LIFECYCLE_TICK_MS and fast-forwards each market through the
// state machine based on wall-clock deltas. Deterministic: no randomness
// in the transitions (except the initial stale seed below).

const LIFECYCLE_TICK_MS = 1000;
let tickHandle: ReturnType<typeof setInterval> | null = null;

function advanceLifecycle(): void {
  const now = Date.now();
  let mutated = false;

  for (const m of MARKETS) {
    if (m.status === 'ACTIVE') {
      // Accrue a nominal Maker Fee during ACTIVE so the Veto burn has
      // something visible to forfeit. Matches the §6 0.5 % Maker Fee
      // shape without needing a real P2P pool to compute against.
      m.makerFeeAccrued += 0.05;

      if (now >= m.scheduledResolutionAt) {
        // Sandbox has no oracle — every ACTIVE market eventually stales.
        // In prod, the Newswire/AI/Weather signal would resolve here.
        m.status  = 'STALE';
        m.staleAt = now;
        mutated   = true;
      }
    } else if (m.status === 'PENDING' && now >= m.scheduledResolutionAt) {
      // Unvouched markets past their scheduled resolution also stale out
      // — they never made it to the Global Lens, but the bond is still
      // locked, so the same escape valves apply.
      m.status  = 'STALE';
      m.staleAt = now;
      mutated   = true;
    } else if (m.status === 'STALE' && m.staleAt !== null) {
      const autoRefundAt = m.staleAt + AUTO_REFUND_AT_HOURS * HOUR_MS;
      if (now >= autoRefundAt) {
        // §6.1 AUTO_REFUNDED — bond returned to creator, Maker Fee
        // forfeited (never distributed, not burned).
        const creator = ensureUserBond(m.creatorId);
        creator.bondsLocked = Math.max(0, creator.bondsLocked - m.creationBond);
        creator.balance    += m.creationBond;
        m.status = 'AUTO_REFUNDED';
        mutated  = true;
      }
    }
  }

  if (mutated) notify();
}

function ensureTickerRunning(): void {
  if (tickHandle !== null) return;
  if (typeof window === 'undefined') return; // SSR / test — no-op
  tickHandle = setInterval(advanceLifecycle, LIFECYCLE_TICK_MS);
}

// ─── Read helpers ───────────────────────────────────────────────────────

export function listMintedMarkets(): MintedMarket[] {
  // Newest first — matches the unshift at mint. Callers filter by status.
  return MARKETS.slice();
}

export function listMintedMarketsForViewer(viewerId: string): MintedMarket[] {
  // For the Queue panel: everything the viewer could meaningfully act on
  // (their own creations + others' PENDING/STALE). Terminal-state markets
  // of other creators are dropped to keep the panel compact.
  return MARKETS.filter(m => {
    if (m.creatorId === viewerId) return true;
    return m.status === 'PENDING' || m.status === 'STALE';
  });
}

// Lifecycle phase info for the Sovereign Veto button. Returns the next
// transition time so the UI can render a countdown.
export interface VetoPhase {
  phase:          'PRE_VETO' | 'VETO_OPEN' | 'AUTO_REFUND_IMMINENT' | 'NOT_STALE';
  opensAt:        number | null;  // ms epoch
  autoRefundAt:   number | null;  // ms epoch
}

export function vetoPhaseFor(m: MintedMarket, now = Date.now()): VetoPhase {
  if (m.status !== 'STALE' || m.staleAt === null) {
    return { phase: 'NOT_STALE', opensAt: null, autoRefundAt: null };
  }
  const opens  = m.staleAt + VETO_OPENS_AT_HOURS  * HOUR_MS;
  const closes = m.staleAt + AUTO_REFUND_AT_HOURS * HOUR_MS;
  if (now < opens)  return { phase: 'PRE_VETO',             opensAt: opens, autoRefundAt: closes };
  if (now < closes) return { phase: 'VETO_OPEN',            opensAt: opens, autoRefundAt: closes };
  return               { phase: 'AUTO_REFUND_IMMINENT',     opensAt: opens, autoRefundAt: closes };
}

// ─── Test / demo helpers ────────────────────────────────────────────────
//
// Exposed on `window.__mint` so the Architect can drop a market
// directly into any state for screenshots without clicking through the
// Vouch / Veto chain. Safe to register unconditionally — no-op on SSR.

if (typeof window !== 'undefined') {
  (window as unknown as { __mint?: Record<string, unknown> }).__mint = {
    mint:  mintMarket,
    list:  listMintedMarkets,
    bonds: (userId: string) => ensureUserBond(userId),
    force: (marketId: string, status: MintStatus): boolean => {
      const m = MARKETS.find(x => x.id === marketId);
      if (!m) return false;
      m.status  = status;
      m.staleAt = status === 'STALE' ? Date.now() : m.staleAt;
      notify();
      return true;
    },
  };
}
